import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';

export type JsonEditorValueType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'null'
  | 'object'
  | 'array';

@Component({
  selector: 'app-json-value-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatIconModule,
    JsonValueEditorComponent,
  ],
  templateUrl: './json-value-editor.component.html',
  styleUrl: './json-value-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JsonValueEditorComponent implements OnInit, OnChanges {
  @Input({ required: true }) value: unknown = null;
  @Input() label = '';
  @Input() depth = 0;
  @Input() maxDepth = 8;
  @Output() valueChange = new EventEmitter<unknown>();

  valueType: JsonEditorValueType = 'null';
  objectKeys: string[] = [];
  arrayItems: unknown[] = [];

  newObjectKey = '';
  newArrayItemType: JsonEditorValueType = 'string';
  newArrayItemValue = '';

  readonly typeOptions: { value: JsonEditorValueType; label: string }[] = [
    { value: 'string', label: 'Tekst' },
    { value: 'number', label: 'Tal' },
    { value: 'boolean', label: 'Sand/falsk' },
    { value: 'null', label: 'Null' },
    { value: 'object', label: 'Objekt' },
    { value: 'array', label: 'Liste' },
  ];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.syncFromValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.syncFromValue();
    }
  }

  get isNested(): boolean {
    return this.depth > 0;
  }

  get canNest(): boolean {
    return this.depth < this.maxDepth;
  }

  onScalarStringChange(raw: string): void {
    this.emitChange(raw);
  }

  onScalarNumberChange(raw: string): void {
    const n = raw === '' ? 0 : Number(raw);
    this.emitChange(Number.isNaN(n) ? 0 : n);
  }

  onScalarBooleanChange(checked: boolean): void {
    this.emitChange(checked);
  }

  onTypeChange(next: JsonEditorValueType): void {
    this.emitChange(this.defaultForType(next));
  }

  onObjectKeyChange(key: string, next: unknown): void {
    if (this.valueType !== 'object' || !this.isPlainObject(this.value)) {
      return;
    }
    const obj = { ...(this.value as Record<string, unknown>) };
    obj[key] = next;
    this.emitChange(obj);
  }

  removeObjectKey(key: string): void {
    if (this.valueType !== 'object' || !this.isPlainObject(this.value)) {
      return;
    }
    const obj = { ...(this.value as Record<string, unknown>) };
    delete obj[key];
    this.emitChange(obj);
  }

  addObjectKey(): void {
    const key = this.newObjectKey.trim();
    if (!key || this.valueType !== 'object' || !this.isPlainObject(this.value)) {
      return;
    }
    const obj = { ...(this.value as Record<string, unknown>) };
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return;
    }
    obj[key] = '';
    this.newObjectKey = '';
    this.emitChange(obj);
  }

  onArrayItemChange(index: number, next: unknown): void {
    if (this.valueType !== 'array' || !Array.isArray(this.value)) {
      return;
    }
    const arr = [...this.value];
    arr[index] = next;
    this.emitChange(arr);
  }

  removeArrayItem(index: number): void {
    if (this.valueType !== 'array' || !Array.isArray(this.value)) {
      return;
    }
    const arr = [...this.value];
    arr.splice(index, 1);
    this.emitChange(arr);
  }

  addArrayItem(): void {
    if (this.valueType !== 'array' || !Array.isArray(this.value)) {
      return;
    }
    const item = this.newArrayItemType === 'string' && this.newArrayItemValue !== ''
      ? this.parseNewArrayScalar()
      : this.defaultForType(this.newArrayItemType);
    this.emitChange([...this.value, item]);
    this.newArrayItemValue = '';
  }

  private parseNewArrayScalar(): unknown {
    if (this.newArrayItemType === 'number') {
      const n = Number(this.newArrayItemValue);
      return Number.isNaN(n) ? 0 : n;
    }
    if (this.newArrayItemType === 'boolean') {
      return this.newArrayItemValue === 'true';
    }
    return this.newArrayItemValue;
  }

  private emitChange(next: unknown): void {
    this.value = next;
    this.syncFromValue();
    this.valueChange.emit(next);
  }

  private syncFromValue(): void {
    this.valueType = this.detectType(this.value);
    if (this.valueType === 'object' && this.isPlainObject(this.value)) {
      this.objectKeys = Object.keys(this.value as Record<string, unknown>).sort((a, b) =>
        a.localeCompare(b, 'da')
      );
    } else {
      this.objectKeys = [];
    }
    if (this.valueType === 'array' && Array.isArray(this.value)) {
      this.arrayItems = this.value;
    } else {
      this.arrayItems = [];
    }
    this.cdr.markForCheck();
  }

  private detectType(value: unknown): JsonEditorValueType {
    if (value === null) {
      return 'null';
    }
    if (Array.isArray(value)) {
      return 'array';
    }
    if (typeof value === 'boolean') {
      return 'boolean';
    }
    if (typeof value === 'number') {
      return 'number';
    }
    if (typeof value === 'string') {
      return 'string';
    }
    if (typeof value === 'object') {
      return 'object';
    }
    return 'string';
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private defaultForType(type: JsonEditorValueType): unknown {
    switch (type) {
      case 'string':
        return '';
      case 'number':
        return 0;
      case 'boolean':
        return false;
      case 'null':
        return null;
      case 'object':
        return {};
      case 'array':
        return [];
      default:
        return '';
    }
  }

  objectValue(key: string): unknown {
    if (!this.isPlainObject(this.value)) {
      return null;
    }
    return (this.value as Record<string, unknown>)[key];
  }
}
